/**
 * External dependencies
 */
import React, { memo, useMemo } from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@naruto/naruto-theme/src/common';
import { AppRouter } from '@naruto/naruto-hooks/src/useRouter';
import ErrorBound from '@naruto/naruto-components/src/common/v1/ErrorBound';
import ModalConfirm from '@naruto/naruto-components/src/common/v1/ModalConfirm';
import ModalDialog from '@naruto/naruto-components/src/common/v1/ModalDialog';
import MessageSnackbar from '@naruto/naruto-components/src/common/v1/MessageSnackbar';

/**
 * Internal dependencies
 */
import SettingsControl from '@naruto/bgdapp/src/components/Common/SettingControl';
import MainCheckView from '@naruto/bgdapp/src/components/Common/CheckView';
import MainView from '@naruto/bgdapp/src/components/Layer/MainView';
import useTheme from '@naruto/bgdapp/src/hooks/useTheme';
import useLang from '@naruto/bgdapp/src/hooks/useLang';
import useData from './useData';

function App(): React.ReactElement {
  /* Global & local State */
  const theme = useTheme();
  const { i18ns } = useLang();
  const {
    userAuthCheck,
    messageDialogList,
    messageConfirmList,
    messageSnackList,
    langOptions,
    sizeOptions,
    currentLang,
    fontScale,
    removeDialog,
    removeConfirm,
    removeSnack,
    handleScaleChange,
    handleLangChange
  } = useData();

  /* Views */
  const renderMainView = useMemo(
    () => (
      <AppRouter basename="">
        <CssBaseline>
          <SettingsControl
            sx={{
              p: 4,
              position: 'fixed',
              top: 12,
              right: 28,
              borderRadius: '8px',
              zIndex: 1300,
              display: {
                xss: 'none',
                xs: 'none',
                sm: 'none',
                md: 'none',
                lg: 'block'
              }
            }}
            sizeLabel={i18ns.__('frontend.global.property.fontsize')}
            languageOptions={langOptions}
            sizeOptions={sizeOptions}
            lang={currentLang}
            scale={fontScale}
            onLangChange={handleLangChange}
            onScaleChange={handleScaleChange}
          />
          {userAuthCheck && <MainView />}
          <ModalDialog list={messageDialogList.list} removeDialog={removeDialog} />
          <ModalConfirm list={messageConfirmList.list} removeConfirm={removeConfirm} />
          <MessageSnackbar list={messageSnackList.list} removeSnack={removeSnack} />
          <Fade in={!userAuthCheck} timeout={500} unmountOnExit>
            <Box
              sx={{
                position: 'fixed', // 固定定位，覆蓋全螢幕
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999, // 確保層級最高，蓋住底下內容
                bgcolor: 'background.default', // 使用主題背景色，確保不透明
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <MainCheckView />
            </Box>
          </Fade>
        </CssBaseline>
      </AppRouter>
    ),
    [
      userAuthCheck,
      messageConfirmList,
      messageDialogList,
      messageSnackList,
      langOptions,
      sizeOptions,
      currentLang,
      fontScale,
      i18ns,
      handleLangChange,
      handleScaleChange,
      removeDialog,
      removeConfirm,
      removeSnack
    ]
  );

  /* Main */
  return (
    <ErrorBound>
      <ThemeProvider theme={theme}>{renderMainView}</ThemeProvider>
    </ErrorBound>
  );
}

export default memo(App);
